/**
 * Guide Matching Utilities
 * Handles matching vehicles to guides with generation-aware year ranges
 */

/**
 * Check if a guide's year range includes the user's vehicle year
 */
export function yearInRange(userYear, guide) {
  // Support both old single-year format and new yearRange format
  if (guide.vehicle.yearRange) {
    const { start, end } = guide.vehicle.yearRange;
    return userYear >= start && userYear <= end;
  }
  // Fallback to exact year match for old format
  return guide.vehicle.year === userYear;
}

/**
 * Check if a guide matches the user's vehicle
 * Accounts for: make, model, year ranges (generations), engine types, and transmission
 */
export function guideMatchesVehicle(guide, vehicle) {
  if (!vehicle) return false;

  // Make and model must match (case-insensitive)
  const makeMatch = guide.vehicle.make?.toLowerCase() === vehicle.make?.toLowerCase();
  const primaryModelMatch = guide.vehicle.model?.toLowerCase() === vehicle.model?.toLowerCase();
  // Support compatibleModels for cross-model guides (e.g. Bullnose F-150 guides
  // that also apply to F-250/F-350)
  const compatibleModelMatch = Array.isArray(guide.vehicle.compatibleModels) &&
    guide.vehicle.compatibleModels.some(m => m.toLowerCase() === vehicle.model?.toLowerCase());
  const modelMatch = primaryModelMatch || compatibleModelMatch;

  if (!makeMatch || !modelMatch) return false;

  // Check generation-specific year ranges
  // Guides can specify an array of generation year ranges
  if (guide.vehicle.generations && Array.isArray(guide.vehicle.generations)) {
    const generationMatch = guide.vehicle.generations.some(gen =>
      vehicle.year >= gen.startYear && vehicle.year <= gen.endYear
    );
    if (!generationMatch) return false;
  } else if (guide.vehicle.yearRange) {
    // Fallback to yearRange format
    const yearMatch = yearInRange(vehicle.year, guide);
    if (!yearMatch) return false;
  } else if (guide.vehicle.year) {
    // Fallback to exact year match
    if (guide.vehicle.year !== vehicle.year) return false;
  }

  // Engine filtering: only filter if user specifically selected one
  if (vehicle.engine && guide.vehicle.engine) {
    if (guide.vehicle.engine !== vehicle.engine) {
      return false;
    }
  }
  // Always show guides if engine isn't specified, or matches

  // Transmission filtering: only filter if user specifically selected one
  if (vehicle.transmission && guide.vehicle.transmission) {
    // Only filter if both have transmission specified
    if (guide.vehicle.transmission !== vehicle.transmission) {
      return false;
    }
  }
  // Always show guides if transmission isn't specified, or matches

  return true;
}

/**
 * Get the generation label for a guide based on year range
 * Used for display purposes
 */
export function getGenerationLabel(guide) {
  if (!guide.vehicle.yearRange) {
    return `${guide.vehicle.year}`;
  }
  const { start, end } = guide.vehicle.yearRange;
  if (start === end) return `${start}`;
  return `${start}-${end}`;
}

/**
 * Get generation-specific content (video, images) from a guide
 * Returns the version matching the user's vehicle year
 */
export function getGenerationContent(userYear, guide, contentType = 'video') {
  if (!guide[contentType]) return null;

  // If content is an array of generation-specific versions
  if (Array.isArray(guide[contentType])) {
    return guide[contentType].find(item =>
      item.generation && yearInRange(userYear, { year: userYear, ...item.generation })
    ) || guide[contentType][0];
  }

  // If content has generation field, check if it matches
  if (guide[contentType].generation) {
    if (yearInRange(userYear, { year: userYear, ...guide[contentType].generation })) {
      return guide[contentType];
    }
    return null;
  }

  // Return content as-is if no generation tagging
  return guide[contentType];
}

/**
 * Get generation-specific step content
 */
export function getGenerationStep(userYear, step) {
  if (!step) return step;

  // If step has generation-specific diagram
  if (step.diagram && step.diagram.generation) {
    if (yearInRange(userYear, { year: userYear, ...step.diagram.generation })) {
      return step;
    }
    // Could return a fallback here if needed
  }

  return step;
}
